"use client"

import * as React from "react"
import { LuCheck, LuChevronDown } from "react-icons/lu"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface ButtonSelectOption<T extends string = string> {
  value: T
  label: string
  disabled?: boolean
  disabledReason?: string
}

interface ButtonSelectProps<T extends string = string> {
  value: T
  onValueChange: (value: T) => void
  options: ButtonSelectOption<T>[]
  placeholder?: string
  className?: string
  size?: "xs" | "sm" | "default"
  icon?: React.ReactNode
  /** Header text shown at the top of the dropdown */
  header?: string
  /** Width of the dropdown content */
  dropdownWidth?: number | string
  /** Additional className for the dropdown content */
  dropdownClassName?: string
  /** Controlled open state */
  open?: boolean
  /** Controlled open state change handler */
  onOpenChange?: (open: boolean) => void
  /** Auto-focus the trigger when true */
  autoFocus?: boolean
}

function ButtonSelect<T extends string = string>({
  value,
  onValueChange,
  options,
  placeholder,
  className,
  size = "xs",
  icon,
  header,
  dropdownWidth = 180,
  dropdownClassName,
  open: controlledOpen,
  onOpenChange,
  autoFocus,
}: ButtonSelectProps<T>) {
  // Initialize open state based on autoFocus - this runs once on mount
  const [internalOpen, setInternalOpen] = React.useState(() => autoFocus ?? false)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  
  // Use controlled or uncontrolled open state
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  
  // Store onOpenChange in ref to avoid effect dependency issues
  const onOpenChangeRef = React.useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  
  const setOpen = React.useCallback((newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen)
    }
    onOpenChangeRef.current?.(newOpen)
  }, [isControlled])

  // Notify parent of initial open state if autoFocus was true
  React.useEffect(() => {
    if (autoFocus) {
      onOpenChangeRef.current?.(true)
    }
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  const selectedOption = options.find(opt => opt.value === value)
  const displayValue = selectedOption?.label || placeholder || "Select..."

  const handleSelect = (option: ButtonSelectOption<T>) => {
    if (option.disabled) return
    onValueChange(option.value)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="transparent-outline"
          size={size}
          className={cn(
            "hover:bg-[#F2EEFF] hover:text-[#654EDA] hover:border-[#E2DAFF]",
            "focus-visible:ring-[#654EDA]/20 focus-visible:border-[#654EDA] focus-visible:ring-2",
            open && "bg-[#E2DAFF] text-[#654EDA] border-[#C6B9FF] hover:bg-[#E2DAFF] focus-visible:ring-0",
            className
          )}
          role="combobox"
          aria-expanded={open}
        >
          {icon}
          {displayValue}
          <LuChevronDown
            className={cn(
              "size-3.5 ml-0.5 transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "p-0 border-[#EDEEF3] shadow-lg flex flex-col overflow-hidden",
          "max-h-[var(--radix-popover-content-available-height,400px)]",
          dropdownClassName
        )}
        style={{ 
          width: typeof dropdownWidth === "number" ? `${dropdownWidth}px` : dropdownWidth,
        }}
        align="start"
        sideOffset={6}
      >
        {/* Header */}
        {header && (
          <div className="px-3 py-2 border-b border-[#EDEEF3] bg-white z-10 shrink-0">
            <span className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
              {header}
            </span>
          </div>
        )}

        {/* Options */}
        <div 
          className="py-1 overflow-y-auto flex-1 min-h-0"
          onWheel={(e) => e.stopPropagation()}
        >
          {options.map((option) => {
            const isSelected = value === option.value
            const isDisabled = option.disabled

            return (
              <div
                key={option.value}
                className={cn(
                  "flex items-center gap-3 px-3 h-[32px] min-h-[32px] cursor-pointer transition-colors shrink-0",
                  isDisabled
                    ? "cursor-not-allowed opacity-50"
                    : "hover:bg-[#F7F8F8]",
                  isSelected && !isDisabled && "bg-[#F7F8F8]"
                )}
                onClick={() => handleSelect(option)}
              >
                <span className={cn(
                  "text-[13px] font-medium flex-1 truncate",
                  isSelected ? "text-[#654EDA]" : "text-[#08090A]",
                  isDisabled && "text-[#8A8F98]"
                )}>
                  {option.label}
                </span>
                {isDisabled && option.disabledReason && (
                  <span className="text-[10px] text-[#8A8F98] bg-[#EDEEF3] px-1.5 py-0.5 rounded">
                    {option.disabledReason}
                  </span>
                )}
                {isSelected && !isDisabled && (
                  <LuCheck className="size-4 text-[#654EDA] shrink-0" />
                )}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { ButtonSelect }
export type { ButtonSelectOption, ButtonSelectProps }
